import * as React from 'react';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportAiBundle, exportAiRun } from '../../Api';
import { AiBundle, AiExportRequest } from '../../models/dtos/AiDto';
import { AiActionError, textError, toAiActionError } from './AiShared';

const pad = (n: number) => String(n).padStart(2, '0');

/** `betup-ai-<what>-YYYYMMDD-HHmm.json` (local time). */
export function aiBundleFileName(what: string, date: Date = new Date()): string {
    const stamp = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
    const safe = what.replace(/[^A-Za-z0-9._-]+/g, '_') || 'bundle';
    return `betup-ai-${safe}-${stamp}.json`;
}

/** File-name part for an export request: `all`, `task-<code>`, `agent-<code>`, `tasks`, `agents`. */
export function aiBundleWhat(request: AiExportRequest): string {
    if (request.all) return 'all';
    const tasks = request.tasks ?? [];
    const agents = request.agents ?? [];
    if (tasks.length === 1 && agents.length === 0) return `task-${tasks[0]}`;
    if (agents.length === 1 && tasks.length === 0) return `agent-${agents[0]}`;
    if (agents.length === 0) return 'tasks';
    if (tasks.length === 0) return 'agents';
    return 'bundle';
}

/** "2 tasks, 1 agent". */
export function describeAiBundle(bundle: Pick<AiBundle, 'tasks' | 'agents'>): string {
    const tasks = Array.isArray(bundle.tasks) ? bundle.tasks.length : 0;
    const agents = Array.isArray(bundle.agents) ? bundle.agents.length : 0;
    return `${tasks} task${tasks === 1 ? '' : 's'}, ${agents} agent${agents === 1 ? '' : 's'}`;
}

/** Saves the bundle as pretty JSON (2 spaces) through a temporary `<a download>`. */
export function downloadAiBundle(bundle: AiBundle, what: string): string {
    return downloadAiJson(bundle, aiBundleFileName(what));
}

/** Saves `value` as pretty JSON (2 spaces) named `fileName` through a temporary `<a download>`. */
export function downloadAiJson(value: unknown, fileName: string): string {
    const blob = new Blob([JSON.stringify(value, null, 2) + '\n'], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    return fileName;
}

export interface AiExportOutcome {
    error: AiActionError | null;
    /** Success text: file name and what it holds. */
    notice: string | null;
}

/** Calls `GET /export` and downloads the result. */
export async function exportAiBundleToFile(request: AiExportRequest): Promise<AiExportOutcome> {
    const result = await exportAiBundle(request);
    if (result.error != null) return { error: toAiActionError(result, 'Export failed'), notice: null };
    if (result.data == null) return { error: textError('Export failed: empty response'), notice: null };
    const fileName = downloadAiBundle(result.data, aiBundleWhat(request));
    return { error: null, notice: `Downloaded ${fileName} (${describeAiBundle(result.data)}).` };
}

/** "Export" button of the task / agent editors: one item, downloaded as a bundle file. */
export function AiExportButton({ request, tooltip, disabled, onOutcome }: {
    request: AiExportRequest;
    tooltip?: React.ReactNode;
    disabled?: boolean;
    onOutcome: (outcome: AiExportOutcome) => void;
}) {
    const [busy, setBusy] = React.useState(false);
    const run = async () => {
        setBusy(true);
        const outcome = await exportAiBundleToFile(request);
        setBusy(false);
        onOutcome(outcome);
    };
    const button = (
        <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <FileDownloadIcon />}
            disabled={disabled || busy} onClick={run}>
            Export
        </Button>
    );
    // Wrapper span: a Tooltip needs an element that receives events even when the button is disabled.
    return tooltip ? <Tooltip title={tooltip}><span>{button}</span></Tooltip> : button;
}

/** Calls `GET /runs/{runId}/export` and downloads it as `run-<runId>.json`. */
export async function exportAiRunToFile(runId: string): Promise<AiExportOutcome> {
    const result = await exportAiRun(runId);
    if (result.error != null) return { error: toAiActionError(result, 'Run export failed'), notice: null };
    if (result.data == null) return { error: textError('Run export failed: empty response'), notice: null };
    const fileName = downloadAiJson(result.data, `run-${runId.replace(/[^A-Za-z0-9._-]+/g, '_')}.json`);
    return { error: null, notice: `Downloaded ${fileName}.` };
}

/** "Export JSON" of one agent run (`iconOnly` for table rows; the click does not reach the row). */
export function AiRunExportButton({ runId, iconOnly = false, onOutcome }: {
    runId: string;
    iconOnly?: boolean;
    onOutcome: (outcome: AiExportOutcome) => void;
}) {
    const [busy, setBusy] = React.useState(false);
    const run = async (event: React.MouseEvent) => {
        event.stopPropagation();
        setBusy(true);
        const outcome = await exportAiRunToFile(runId);
        setBusy(false);
        onOutcome(outcome);
    };
    if (iconOnly) {
        return (
            <Tooltip title="Export JSON (run, totals, agent settings, model / tool calls, conversation, child runs)">
                <span>
                    <IconButton size="small" disabled={busy} onClick={run}>
                        {busy ? <CircularProgress size={16} /> : <FileDownloadIcon fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>
        );
    }
    return (
        <Tooltip title="One JSON file for analysis: the run, totals, the agent's current settings, model and tool calls, the conversation and child runs">
            <span>
                <Button startIcon={busy ? <CircularProgress size={16} /> : <FileDownloadIcon />} disabled={busy} onClick={run}>
                    Export JSON
                </Button>
            </span>
        </Tooltip>
    );
}
